import { appConfig as $config } from '@App/app.config';
import { UI } from '@Modules/UI';
import { Common } from '@Modules/Common';
import { User } from '@Modules/User';
import { App } from '@Modules/App';
import { assetsManager } from '@Modules/assetsManager/artworks';
import { ValidationRules } from '@Modules/ValidationRules';
import { DistributionTerritories } from '@Modules/DistributionTerritories';
import { DistributionChannels } from '@Modules/DistributionChannels';
import { inputCharLimit } from '@Modules/InputCharLimit';
import { Blanks } from '@Pages/Blanks';
import { LicensorRestrictions } from '../../../modules/LicensorRestrictions';

import { mapping } from './mapping';
import { artworkPlacement, artworkType, colorGroupsMap, mockupsPanels, printArea, TABLE_COLUMNS } from './constants';

import { patternArtwork } from './pattern-artwork';
import { complexArtwork } from './complex-artwork';

export const edit = (function () {
  let $isLicensor = '';
  let $artworkType = '';
  let $generateSettings = {};

  let page = (id = false, accountId = '', action = '') => {
    UI.block('#page');
    $isLicensor = User.isLicensor();
    $generateSettings = {};
    $artworkType = getArtworkType(action);

    let queryPath = id ? `/artworks/${accountId}/${id}` : `/artworks/emptyArtwork/${User.accountID()}`;
    $.when(App.ajaxData(queryPath))
      .done((res) => {
        let artwork = mapping.artworkEdit(res, $isLicensor);
        let areaSizes = res.defaults.find((def) => def.key === 'ImageAreaSize').values;
        // Open page
        let template = getArtworkTemplate();

        const defaults = res.defaults ? mapping.defaults(res.defaults) : false;

        UI.page(template, {
          artwork: artwork,
          artworkType: $artworkType,
          mockupsPanels: mockupsPanels,
          artworkPlacement: artworkPlacement,
          defaults: defaults,
          printArea: printArea,
          isLicensor: $isLicensor,
          isLicensorSelectDisabled: $isLicensor || !!artwork.id || !artwork.accountId,
          colorGroups: (res.colorGroups || []).map((colorGroup) => ({
            name: colorGroup,
            label: colorGroupsMap[colorGroup]
          })),
          filesSelectList: mapping.filesSelectList(),
        }).then(() => {
          // set artwork to global object
          App.pageData('currenteditedcontent', artwork);
          App.pageData('currenteditedcontent.versionFiles', artwork.versionFiles);
          App.pageData('currenteditedcontent.versionFilesStringified', JSON.stringify(artwork.versionFiles));

          App.pageData('currenteditedcontent.subscriptionPackages', artwork.subscriptionPackages);
          App.pageData('currenteditedcontent.defaults', defaults);
          App.pageData($config.GLOBAL.UNSAVED_CHANGES, false);
          editPageInit(artwork, areaSizes);
        });
      })
      .always((error) => {
        UI.unblockPage();
      });
  };

  /*
   ** -- Add / Edit Artwork
   **/
  let editPageInit = (artwork, areaSizes) => {
    portletBlanksInit(artwork);
    defaultMockupArtworkSelectInit();
    defaultMockupBlanksSelectInit();

    $('#page')
      .off()
      .on('click', '.action-edit-territories', editTerritoriesDialog)
      .on('click', '.action-edit-channel', editChannelsDialog)
      .on('click', '.action-edit-blank-categories', editBlankCategoriesDialog)
      .on('click', '.action-edit-colors', editColorsDialog)
      .on('click', '.action-edit-blanks', editBlanksDialog)
      .on('click', '.action-edit-distChanel', editDistChanelDialog);

    portletDescriptionInit();

    portletFilesInit(artwork, areaSizes);

    $('.styleguide-select-control, .minprice-select-control').select2();

    setTimeout(() => {
      $('.trims-select-control, .stamps-select-control').select2();
    }, 30);

    $('.js-artwork-save').on('click', artworkEditFormSubmit);
    $('.js-artwork-cancel').on('click', (e) => {
      App.pageData($config.GLOBAL.UNSAVED_CHANGES, false);
      $router.push(`/artworks?restoreQuery`); // Navigate back and restore query
    });

    if ($artworkType === artworkType.pattern) {
      patternArtwork.init(artwork);
    }

    if ($artworkType === artworkType.complex) {
      complexArtwork.init(artwork, {
        portletDescriptionInit
      });
    }
  };

  const portletDescriptionInit = () => {
    $('.js-artwork-generate').off().on('click', openGenerateSettings);

    let $form = $('#artworkDialogForm');
    // Tags input - Select2
    $('[name="meta"]', $form)
      .select2({
        placeholder: 'Add a Tag',
        tags: true,
        tokenSeparators: [',']
      })
      .on('change', Common.select2focus);

    // Collections input - Select2
    $('[name="collections"]', $form)
      .select2({
        placeholder: 'Add collection',
        tags: true,
        tokenSeparators: [',']
      })
      .on('change', Common.select2focus);

    // Autocomplete
    teapaheadInit();

    // Chars Limit
    inputCharLimit.init('[data-charlimit]');

    $('#page')
      .on('change', '#artworkDialogForm > *:not(.artwork-assets, .pattern-artwork-file, .complex-artwork-file) .form-control', () => {
        const currentAction = $('body[current-page="artworks"]').attr('current-action');

        if (!currentAction.includes('add') && !currentAction.includes('edit')) {
          return;
        }

        App.pageData($config.GLOBAL.UNSAVED_CHANGES, true);
      });

    $('.account-select-control').select2();
  };

  const portletFilesInit = (artwork, areaSizes) => {
    let aM = assetsManager.init({
      files: [...artwork.files, ...artwork.screenPrintingFiles],
      versionFiles: artwork.versionFiles,
      screenPrintingFiles: artwork.screenPrintingFiles,
      checkedPartTypes: artwork.checkedPartTypes,
      pageType: 'Artworks',
      areaSizes: areaSizes,
      update: (result) => {
        App.pageData('currenteditedcontent.files', result.files);
        App.pageData('currenteditedcontent.screenPrintingFiles', result.screenPrintingFiles);
        App.pageData('currenteditedcontent.checkedPartTypes', result.checkedPartTypes);
        // Count of replaced Mockups If was replaced Source file
        App.pageData(
          'currenteditedcontent.replacedMockupsCountMap',
          Object.fromEntries(result.replacedMockupsCountMap || new Map())
        );
        App.pageData($config.GLOBAL.UNSAVED_CHANGES, true);
      }
    });
    App.pageData('currenteditedcontent.files', aM.getFiles().files);
  };

  const portletBlanksInit = (artwork) => {
    $('#artworkEditBlanksTbl').DataTable({
      columns: TABLE_COLUMNS.BLANKS,
      data: artwork.selectBlanks,
      responsive: {
        details: {
          type: 'column',
          target: 0
        }
      },
      pageLength: 25,
      cache: false,
      searching: false,
      ordering: false,
      sorting: false,
      dom: '<"row"<"col text-right"p>><"row"<"col"t>>', //<"row"<"col"B>>
      drawCallback: () => {
        UI.unblock('#page');
        // Image popup
        $('.magnific-image').magnificPopup({ type: 'image' });
        $('[data-toggle="popover"]').popover();
      },
      initComplete: () => {
      }
    });
  };

  const defaultMockupArtworkSelectInit = () => {
    $('#defaultMockupDropdown')
      .select2()
      .on('change', (e) => {
        const val = e.target.value || mockupsPanels[0].key;

        App.pageData('currenteditedcontent.defaultMockupPanel', val);

        $('.mockup-select').val(val).trigger('change');
      });
  };

  const defaultMockupBlanksSelectInit = () => {
    $('.mockup-select')
      .select2()
      .on('change', (e) => {
        const target = $(e.target);
        const lineItemId = target.data('lineiteminstoreid');

        let selectBlanks = App.pageData('currenteditedcontent.selectBlanks') || [];
        selectBlanks.some((sb) => {
          if (sb.id === lineItemId) {
            sb.defaultMockupPanel = e.target.value || mockupsPanels[0].key;
            return true;
          }
        });

        App.pageData('currenteditedcontent.selectBlanks', selectBlanks);
      });
  };

  const teapaheadInit = () => {
    let throttledBrandRequest = _.debounce(function (query, processSync, processAsync) {
      const accountId = $('[name="licensorId"]', $('#artworkDialogForm')).val();

      $.when(
        App.ajaxData(`Autocompele/get?propertyName=brand&term=${query}&accountId=${accountId}&requestPage=Artworks`)
      ).done((res) => processAsync(res?.items?.map((i) => i.propertyValue)));
    }, 300);

    $('[name="brand"]').typeahead(
      {
        hint: true,
        highlight: true,
        minLength: 1
      },
      {
        name: 'brand',
        async: true,
        source: function (query, processSync, processAsync) {
          throttledBrandRequest(query, processSync, processAsync);
        },
        templates: {
          empty: '<div>no result</div>',
          pending: '<div>Please wait...</div>'
        }
      }
    );

    $('[name="brand"]').bind('typeahead:change', function (ev, suggestion) {
      setTimeout(function () {
        $('[name="subBrand"]').typeahead('val', '');
      }, 100);
    });
  };

  let artworkEditFormSubmit = (e) => {
    if (($artworkType !== artworkType.pattern && $artworkType !== artworkType.complex)) {
      if (App.pageData('currenteditedcontent.unsavedVersionFilesChanges')) {
        const savedVersionFiles = JSON.parse(App.pageData('currenteditedcontent.versionFilesStringified') || '[]');

        if (savedVersionFiles.length > 0) {
          unsavedArtworkVersionDialog(() => {
            assetsManager.restoreChanges();
            artworkEditFormSubmit();
          });
        } else {
          $('#noArtworkVersion').show();
          $('.artwork-assets > .kt-portlet').addClass('invalid');

          UI.scrollToElement('#noArtworkVersion');
        }

        return;
      }
    }

    let $form = $('#artworkDialogForm');

    // Validate
    $form.validate({ rules: ValidationRules.artworkDialogForm });
    if (!$form.valid()) {
      UI.scrollToInvalid();
      return;
    }

    let invalid = false;

    if ($artworkType === artworkType.pattern) {
      invalid = invalid || !patternArtwork.isValid();
    }

    if ($artworkType === artworkType.complex) {
      const saveFirstTemplatePromise = complexArtwork.generateFirstTemplateAsset();

      if (saveFirstTemplatePromise) {
        UI.blockPage();

        return saveFirstTemplatePromise.then(() => {
          UI.unblockPage();
          artworkEditFormSubmit();
        }).catch(UI.unblockPage);
      }

      invalid = invalid || !complexArtwork.isValid();
    }

    if (invalid) {
      return;
    }

    UI.blockPage();

    const formData = mapping.artworkSave($form.serializeObject(), $isLicensor, $artworkType);
    const replacedMockupsCount = App.pageData('currenteditedcontent.replacedMockupsCountMap');

    // Number of screenprint Colors Validation
    validateNumberColors(formData)
      .then((formData) => {
        if (Object.values(replacedMockupsCount || {}).filter((m) => m > 0).length) {
          confirmReplaceArtworkDialog(formData, replacedMockupsCount);
        } else {
          createOrUpdateArtwork(formData);
        }
      })
      .catch(() => {
        UI.unblockPage();
      });
  };

  let openGenerateSettings = () => {
    const template = require('./tmpl/dialog/generate-settings.hbs');
    const currentArtwork = App.pageData('currenteditedcontent');

    $generateSettings.keywords = $generateSettings.keywords || currentArtwork.title;

    const data = {
      $lang,
      generateSettings: $generateSettings,
    };

    bootbox
      .dialog({
        title: $lang.artwork.GenerateSettings.GenerateSettings,
        message: template(data),
        size: 'medium',
        onHide: () => $generateSettings = $('#generate-settings').serializeObject(),
        buttons: {
          ok: {
            label: $lang.btn.ok,
            className: 'btn btn-primary generate-settings-ok',
            callback: () => setTimeout(() => centralArtworkGenerateAIDescription())
          }
        }
      })
      .init(() => $('#generate-settings [name="keywords"]').on('change', (e) => {
        $('.generate-settings-ok').prop('disabled', !e.target.value);
      }));
  };

  let centralArtworkGenerateAIDescription = () => {
    let $form = $('#artworkDialogForm');

    UI.blockPage();

    const formData = {
      ...mapping.artworkSave($form.serializeObject(), $isLicensor, $artworkType),
      title: $generateSettings.keywords,
    };

    $.when(App.ajaxData('artworks/generateContent', { EntityType: 'Artworks', Entity: formData }, 'POST'))
      .done((res) => {
        if (!res.isSuccess) {
          return validationErrorMessage(res);
        }

        const currentArtwork = App.pageData('currenteditedcontent');

        const updatedValues = ['description', 'bulletPoints', 'tags', 'title']
          .reduce((acc, formInputName) => {
            if ($generateSettings[formInputName] === 'on') {
              return acc;
            }

            if (formInputName === 'tags') {
              acc.tags = Object.keys(res.object.keywords || {}).reduce((acc, key) => acc.concat(res.object.keywords[key]), []);
              return acc;
            }

            acc[formInputName] = res.object[formInputName];
            return acc;
          }, {});

        Object.assign(currentArtwork, updatedValues);

        const data = {
          $lang,
          artwork: currentArtwork,
          artworkType: artworkType.central,
          isLicensorSelectDisabled: User.isLicensor() || !!currentArtwork.id || !currentArtwork.accountId
        };

        App.pageDataInit(data);

        let template = require(`./tmpl/partial/description.hbs`);
        $('#descriptionContainer').empty().html(template(data));

        dataBinder.dataBind(document.body, window);
        App.pageData('currenteditedcontent', currentArtwork);

        portletDescriptionInit();
      })
      .always(() => {
        UI.unblockPage();
      });
  };

  let createOrUpdateArtwork = (formData) => {
    UI.blockPage();
    let url = formData.id
      ? `/artworks/update/`
      : `/artworks/create/${formData.accountId ? formData.accountId : User.accountID()}`;
    $.when(App.ajaxData(url, formData, 'POST'))
      .done((res) => {
        if (!res.isSuccess) {
          return validationErrorMessage(res);
        }

        App.pageData($config.GLOBAL.UNSAVED_CHANGES, false);

        bootbox.hideAll();

        $router.push(`/artworks?restoreQuery`);

        UI.notify({
          text: $lang.alert.SuccessfullySaved,
          icon: 'fal fa-check-circle',
          type: 'success'
        });
      })
      .always((error) => {
        UI.unblockPage();
        UI.unblock('.modal-content');
      });
  };

  let confirmReplaceArtworkDialog = (formData, replacedMockupsCount) => {
    let fileName;
    formData.files.forEach((file) => {
      if (file.IsAdd && file?.additionalValues.srcFile) {
        fileName = file.fileName;
      }
    });
    UI.unblockPage('#page');

    const versionFiles = App.pageData('currenteditedcontent.versionFiles');

    bootbox.dialog({
      title: `<i class="fal fa-exclamation-triangle text-danger mr-2"></i> ${$lang.artwork.replaceArtworkTitle}`,
      message: Object.keys(replacedMockupsCount)
        .filter((k) => replacedMockupsCount[k] > 0)
        .map((k) =>
          $lang.artwork.replaceArtworkText
            .replace('%mockups%', replacedMockupsCount[k])
            .replace('%versionName%', versionFiles.find((v) => v.colorGroup === k)?.name)
        )
        .join(''),
      size: 'large',
      onHidden: () => UI.unblock('#page'),
      buttons: {
        ok: {
          label: $lang.artwork.ContinueReplaceBtn,
          className: 'btn btn-danger',
          callback: () => createOrUpdateArtwork(formData)
        },
        cancel: {
          label: $lang.btn.cancel,
          className: 'btn btn-link'
        }
      }
    });
  };

  let validateNumberColors = (formData) => {
    return new Promise((resolve, reject) => {
      const { checkedPartTypes, screenNumberOfColors, screenPrintingFiles } = formData;
      const screenPrintChecked = (checkedPartTypes & 2) === 2;
      if (!screenPrintChecked) {
        resolve(formData);
        return;
      }
      if (!(screenNumberOfColors * 1) || screenNumberOfColors * 1 === screenPrintingFiles.length) {
        formData.screenNumberOfColors = screenPrintingFiles.length;
        resolve(formData);
        return;
      }
      UI.unblockPage();

      resolve(formData);
    });
  };

  let validationErrorMessage = (res) => {
    if (res.validationErrors) {
      let template = require('./tmpl/validationDialog.hbs');
      let title = '';

      switch (res.validationErrors[0].errorType) {
        case 1:
          title = $lang.artwork.FilesMissing;
          break;
        default:
          title = $lang.artwork.Error;
          break;
      }
      bootbox.dialog({
        size: 'medium',
        title: title,
        value: null,
        message: template(res.validationErrors),
        buttons: {
          cancel: {
            label: $lang.btn.gotIt,
            className: 'btn btn-brand'
          }
        }
      });
      UI.unblock('#page');
      return UI.unblock('.modal-content');
    } else {
      return UI.errorMessage(res.errorInformation.errorReason);
    }
  };

  let editBlanksDialog = (e) => {
    UI.block('#page');

    let selectBlanks = App.pageData('currenteditedcontent.selectBlanks') || [];
    // INFO: outdated functionality. editArtworkBlanks renamed to editArtworkBlanksIds. Uses during Creating Mockup from artwork
    App.pageData('editArtworkBlanks', selectBlanks);

    Blanks.init({
      action: 'dialog',
      selectBlanks: selectBlanks.map((blank) => blank.id),
      isDetachedFromAccountBlanks: App.pageData('currenteditedcontent.isDetachedFromAccountBlanks') || false,
      callback: () => {
        let selectBlanks = App.pageData('editArtworkBlanks');

        selectBlanks = mapping.selectBlanks(selectBlanks, App.pageData('currenteditedcontent.defaultMockupPanel'));

        App.pageData('currenteditedcontent.selectBlanks', selectBlanks);
        App.pageData('editArtworkBlanks', []);
        App.pageData($config.GLOBAL.UNSAVED_CHANGES, true);

        $('#artworkEditBlanksTbl').DataTable().clear().rows.add(selectBlanks).draw();
        defaultMockupBlanksSelectInit();
      }
    });
  };

  let editTerritoriesDialog = (e) => {
    e.preventDefault();

    DistributionTerritories.dialog({
      // Territories dialog data
      data: {
        continents: mapping.territories(App.pageData('currenteditedcontent.defaults.DistributionTerritories')),
        selected: App.pageData('currenteditedcontent.licensorRestrictions.territories'),
        $lang: $lang
      },
      // setTerritories
      ok: () => {
        let $form = $('#territoriesDialog'),
          newArr = [];
        if ($('[name="regions"]:checked', $form).val() == 'Specific') {
          $('input[type="checkbox"].js-region:checked', $form).each(function () {
            newArr.push($(this).prop('name'));
          });
        } else {
          newArr = ['Worldwide'];
        }
        App.pageData('currenteditedcontent.licensorRestrictions.territories', newArr);
        App.pageData($config.GLOBAL.UNSAVED_CHANGES, true);
      },
      init: () => {
        UI.unblock('#page');
      }
    });
  };

  let editChannelsDialog = (e) => {
    e.preventDefault();

    editLicensorRestrictionsDialog({
      title: $lang.artwork.Channels,
      defaultsPath: 'currenteditedcontent.defaults.ChannelsRestrictions',
      selectedPath: 'currenteditedcontent.licensorRestrictions.channels',
      namesPath: 'currenteditedcontent.licensorRestrictionsNames.channels'
    });
  };

  let editBlankCategoriesDialog = (e) => {
    e.preventDefault();

    editLicensorRestrictionsDialog({
      title: $lang.artwork.BlankCategories,
      defaultsPath: 'currenteditedcontent.defaults.BlankCategoriesRestrictions',
      selectedPath: 'currenteditedcontent.licensorRestrictions.blankCategories',
      namesPath: 'currenteditedcontent.licensorRestrictionsNames.blankCategories'
    });
  };

  let editColorsDialog = (e) => {
    e.preventDefault();

    editLicensorRestrictionsDialog({
      title: $lang.artwork.Colors,
      defaultsPath: 'currenteditedcontent.defaults.ColorsRestrictions',
      selectedPath: 'currenteditedcontent.licensorRestrictions.colors',
      namesPath: 'currenteditedcontent.licensorRestrictionsNames.colors'
    });
  };

  let editLicensorRestrictionsDialog = ({ title, defaultsPath, selectedPath, namesPath }) => {
    LicensorRestrictions.dialog({
      data: {
        title,
        continents: mapping.licensorRestrictions(App.pageData(defaultsPath)),
        selected: mapping.licensorRestrictionsSelected(App.pageData(selectedPath), App.pageData(defaultsPath))
      },
      ok: () => {
        let $form = $('#licensorRestrictions');
        let idxArray = [];

        if ($('[name="licensor-restrictions"]:checked', $form).val() == 'Specific') {
          $('input[type="checkbox"].js-licensor-restriction:checked', $form).each(function () {
            idxArray.push($(this).data('idx'));
          });
        } else {
          idxArray = ['All'];
        }

        const selected = mapping.getLicensorRestrictions(idxArray, App.pageData(defaultsPath));

        App.pageData(selectedPath, selected);
        App.pageData(namesPath, mapping.getLicensorRestrictionsNames(selected, App.pageData(defaultsPath)));
        App.pageData($config.GLOBAL.UNSAVED_CHANGES, true);
      }
    });
  };

  let unsavedArtworkVersionDialog = (callback) => {
    bootbox.dialog({
      title: 'Unsaved artwork version - Continue without it?',
      message:
        '<p>You have 1 unsaved artwork version.</p><p>If you decide to continue, the data you entered so far will not be saved. <br>You will not be able to recover it.</p>',
      size: 'large',
      buttons: {
        ok: {
          label: 'Continue',
          className: 'btn btn-primary',
          callback: () => {
            callback();
          }
        },
        cancel: {
          label: 'Go back',
          className: 'btn btn-link'
        }
      }
    });
  };

  // TODO check DistributionChannels data
  let editDistChanelDialog = (e) => {
    e.preventDefault();
    let stores = [];
    let distributionChannels = App.pageData('currenteditedcontent.selectDistributionChannels') || [];
    distributionChannels.forEach((i) => stores.push({ item1: '', item2: i }));
    App.pageData('selectDistChannels', stores);

    let dialogData = {
      element: App.pageData('currenteditedcontent.defaults.AvailableDistributionChannels'),
      $lang: $lang
    };

    DistributionChannels.dialog({
      data: dialogData,
      accountId: App.pageData('currenteditedcontent.accountId'),
      ok: () => {
        $('#artworkDialogForm').parents('.modal ').show();
        let stwl = [];
        stores = App.pageData('selectDistChannels');
        stores.forEach((store) => {
          stwl.push(store.item2);
        });
        App.pageData('currenteditedcontent.selectDistributionChannels', stwl);
        App.pageData('selectDistChannels', []);
        App.pageData($config.GLOBAL.UNSAVED_CHANGES, true);
      },
      close: () => {
        $('#artworkDialogForm').parents('.modal ').show();
      },
      init: () => {
        UI.unblock('#page');
        $('#artworkDialogForm').parents('.modal ').hide();
      }
    });
  };

  let getArtworkTemplate = () => {
    switch ($artworkType) {
      case artworkType.pattern:
        return require('./tmpl/editform-pattern.hbs');
      case artworkType.complex:
        return require('./tmpl/editform-complex.hbs');
      default:
        return require('./tmpl/editform-central.hbs');
    }
  };

  let getArtworkType = (action) => {
    switch (action) {
      case 'add-central':
      case 'edit-central':
        return artworkType.central;
      case 'add-pattern':
      case 'edit-pattern':
        return artworkType.pattern;
      case 'add-complex':
      case 'edit-complex':
        return artworkType.complex;
    }
  };

  let clear = () => {
    complexArtwork.clear();
  };

  return {
    page,
    clear
  };
})();
